// utils/env.ts
// Utilitaire pour gérer les variables d'environnement de manière type-safe

interface EnvConfig {
  API_URL: string;
  RECAPTCHA_SITE_KEY: string;
  APP_NAME: string;
  APP_VERSION: string;
  ENVIRONMENT: 'development' | 'production' | 'staging';
  DEBUG_MODE: boolean;
  GOOGLE_ANALYTICS_ID?: string;
  HOTJAR_ID?: string;
}

class EnvironmentManager {
  private config: EnvConfig;

  constructor() {
    this.config = this.loadConfig();
  }

  private loadConfig(): EnvConfig {
    // Détection automatique de l'environnement
    const isDev = this.isDevelopment();
    const isProd = this.isProduction();
    
    return {
      // API URL avec fallback intelligent
      API_URL: this.getApiUrl(isDev),
      
      // reCAPTCHA
      RECAPTCHA_SITE_KEY: import.meta.env.VITE_RECAPTCHA_SITE_KEY || 
                         '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI', // Test key
      
      // App info
      APP_NAME: import.meta.env.VITE_APP_NAME || 'Neoqualis',
      APP_VERSION: import.meta.env.VITE_APP_VERSION || '1.0.0',
      ENVIRONMENT: isDev ? 'development' : isProd ? 'production' : 'staging',
      
      // Debug
      DEBUG_MODE: import.meta.env.VITE_DEBUG_MODE === 'true' || isDev,
      
      // Analytics (optionnel)
      GOOGLE_ANALYTICS_ID: import.meta.env.VITE_GOOGLE_ANALYTICS_ID,
      HOTJAR_ID: import.meta.env.VITE_HOTJAR_ID
    };
  }

  private getApiUrl(isDev: boolean): string {
    // ✅ Priorité absolue à VITE_API_URL si définie
    if (import.meta.env.VITE_API_URL) {
      return import.meta.env.VITE_API_URL;
    }
    
    // En développement, utiliser l'URL de dev
    if (isDev) {
      return import.meta.env.VITE_API_URL_DEV || 
             'http://localhost:4000/api';
    }
    
    // En production, utiliser l'URL de prod
    return import.meta.env.VITE_API_URL_PROD || 
           'https://neoqualis-backend-v2-mail-production.up.railway.app/api';
  }

  private isDevelopment(): boolean {
    // ✅ Forcer la production si on est sur Railway
    if (window.location.hostname.includes('railway.app') || 
        window.location.hostname.includes('neoqualis.com')) {
      return false;
    }
    
    return import.meta.env.DEV || 
           import.meta.env.VITE_APP_ENVIRONMENT === 'development' ||
           window.location.hostname === 'localhost' ||
           window.location.hostname === '127.0.0.1';
  }

  private isProduction(): boolean {
    // ✅ Considérer Railway comme production
    if (window.location.hostname.includes('railway.app') || 
        window.location.hostname.includes('neoqualis.com')) {
      return true;
    }
    
    return import.meta.env.PROD || 
           import.meta.env.VITE_APP_ENVIRONMENT === 'production';
  }

  // Getters publics
  get apiUrl(): string {
    return this.config.API_URL;
  }

  get recaptchaSiteKey(): string {
    return this.config.RECAPTCHA_SITE_KEY;
  }

  get appName(): string {
    return this.config.APP_NAME;
  }

  get appVersion(): string {
    return this.config.APP_VERSION;
  }

  get environment(): string {
    return this.config.ENVIRONMENT;
  }

  get isDebug(): boolean {
    return this.config.DEBUG_MODE;
  }

  get googleAnalyticsId(): string | undefined {
    return this.config.GOOGLE_ANALYTICS_ID;
  }

  get hotjarId(): string | undefined {
    return this.config.HOTJAR_ID;
  }

  // Méthodes utilitaires
  isTestRecaptcha(): boolean {
    return this.config.RECAPTCHA_SITE_KEY === '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';
  }

  log(message: string, ...args: any[]): void {
    if (this.config.DEBUG_MODE) {
      console.log(`[${this.config.APP_NAME}]`, message, ...args);
    }
  }

  warn(message: string, ...args: any[]): void {
    if (this.config.DEBUG_MODE) {
      console.warn(`[${this.config.APP_NAME}]`, message, ...args);
    }
  }

  error(message: string, ...args: any[]): void {
    console.error(`[${this.config.APP_NAME}]`, message, ...args);
  }

  // Affichage de la configuration (debug)
  debugConfig(): void {
    if (this.config.DEBUG_MODE) {
      console.group(`🔧 Configuration ${this.config.APP_NAME}`);
      console.log('Environment:', this.config.ENVIRONMENT);
      console.log('API URL:', this.config.API_URL);
      console.log('reCAPTCHA:', this.isTestRecaptcha() ? 'Test Key' : 'Production Key');
      console.log('Debug Mode:', this.config.DEBUG_MODE);
      console.log('Version:', this.config.APP_VERSION);
      console.log('Hostname:', window.location.hostname);
      console.groupEnd();
    }
  }
}

// Instance singleton
export const env = new EnvironmentManager();

// Export des valeurs pour compatibilité
export const {
  apiUrl,
  recaptchaSiteKey,
  appName,
  appVersion,
  environment,
  isDebug
} = env;

// Export par défaut
export default env;
